import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UserAvatar from './UserAvatar';
import ChatWindow from './ChatWindow';
import EntityGroupPanel from './EntityGroupPanel';
import {createOrGetOneOnOneChat, deleteChat} from '../firebase/firestore/chats';
import {useSession} from '../firebase/UserProvider';
import useChatStore from '../stores/useChatStore';
import useAiWidgetStore from '../stores/useAiWidgetStore';
import {listTeamGroups, searchTeam} from '../utils/api/team';
import {entityGroupKey, formatEntityGroupLabel, openEntityGroupChat} from '../utils/openEntityGroupChat';

function mergeGroups(serverGroups, localGroups) {
    const byKey = new Map();
    for (const g of serverGroups || []) {
        const key = entityGroupKey({entityType: g.entityType, entityId: g.entityId});
        if (key) byKey.set(key, g);
    }
    for (const g of localGroups || []) {
        const key = entityGroupKey(g);
        if (key && !byKey.has(key)) byKey.set(key, g);
    }
    return Array.from(byKey.values());
}

export default function TeamTab({chats, loading, error, refetch}) {
    const {user: currentUser} = useSession() || {};
    const myUid = currentUser?.uid || currentUser?.id || null;

    const removeChat = useChatStore((s) => s.removeChat);
    const activeEntityGroup = useAiWidgetStore((s) => s.activeEntityGroup);
    const clearActiveEntityGroup = useAiWidgetStore((s) => s.clearActiveEntityGroup);

    const [activeChat, setActiveChat] = useState(null);
    const [groups, setGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [groupsError, setGroupsError] = useState(null);
    const [localGroups, setLocalGroups] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [creatingId, setCreatingId] = useState(null);
    const [deletingChatId, setDeletingChatId] = useState(null);
    const deletingRef = useRef(false);
    const searchAbortRef = useRef(null);

    const loadGroups = useCallback(async () => {
        setGroupsLoading(true);
        setGroupsError(null);
        try {
            const data = await listTeamGroups();
            setGroups(data);
        } catch (err) {
            console.error('Failed to load team groups:', err);
            setGroupsError(err);
        } finally {
            setGroupsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    useEffect(() => {
        if (!searchFocused) return undefined;
        const q = searchQuery.trim();
        if (!q) {
            setSearchResults([]);
            setSearchLoading(false);
            return undefined;
        }

        searchAbortRef.current?.abort();
        const controller = new AbortController();
        searchAbortRef.current = controller;
        setSearchLoading(true);

        const timer = setTimeout(async () => {
            try {
                const items = await searchTeam(q, {signal: controller.signal});
                if (!controller.signal.aborted) {
                    setSearchResults(items);
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Team search failed:', err);
                    setSearchResults([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setSearchLoading(false);
                }
            }
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [searchQuery, searchFocused]);

    const allGroups = useMemo(
        () => mergeGroups(groups, localGroups),
        [groups, localGroups],
    );

    const ensureGroupListed = useCallback((entityType, entityId, displayTitle) => {
        const key = entityGroupKey({entityType, entityId});
        if (!key) return;
        setLocalGroups((prev) => {
            if (prev.some((g) => entityGroupKey(g) === key)) return prev;
            return [...prev, {
                entityType,
                entityId,
                displayTitle: displayTitle || 'Untitled',
                label: formatEntityGroupLabel(entityType, displayTitle),
            }];
        });
    }, []);

    const handleOpenEntityGroup = useCallback((entityType, entityId, displayTitle) => {
        ensureGroupListed(entityType, entityId, displayTitle);
        setActiveChat(null);
        openEntityGroupChat({entityType, entityId, title: displayTitle});
    }, [ensureGroupListed]);

    const handleSearchSelect = async (hit) => {
        if (!hit?.type || !hit?.id) return;
        if (hit.type === 'person') {
            const otherId = hit.id;
            if (!otherId || !myUid) return;
            setCreatingId(otherId);
            try {
                const chat = await createOrGetOneOnOneChat(
                    {id: otherId, displayName: hit.displayTitle, email: hit.email},
                    currentUser,
                );
                if (chat) {
                    refetch();
                    setSearchQuery('');
                    setSearchFocused(false);
                    clearActiveEntityGroup();
                    setActiveChat(chat);
                }
            } catch (err) {
                console.error('Error creating chat', err);
            } finally {
                setCreatingId(null);
            }
            return;
        }

        if (hit.type === 'chart' || hit.type === 'product') {
            handleOpenEntityGroup(hit.type, hit.id, hit.displayTitle);
            setSearchQuery('');
            setSearchFocused(false);
        }
    };

    const handleDeleteChat = useCallback(async (e, chatIdToDelete) => {
        e.stopPropagation();
        if (deletingRef.current) return;
        deletingRef.current = true;
        setDeletingChatId(chatIdToDelete);
        try {
            await deleteChat(chatIdToDelete);
            removeChat(chatIdToDelete);
            refetch();
        } catch (err) {
            console.error('Error deleting chat', err);
        } finally {
            deletingRef.current = false;
            setDeletingChatId(null);
        }
    }, [removeChat, refetch]);

    const showDropdown = searchFocused && (searchLoading || searchQuery.trim() || searchResults.length > 0);

    if (activeEntityGroup?.entityId) {
        return (
            <EntityGroupPanel
                entityGroup={activeEntityGroup}
                onBack={() => clearActiveEntityGroup()}
            />
        );
    }

    if (activeChat) {
        return (
            <ChatWindow
                chat={activeChat}
                onBack={() => setActiveChat(null)}
                onClose={() => setActiveChat(null)}
            />
        );
    }

    return (
        <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
            <ClickAwayListener onClickAway={() => setSearchFocused(false)}>
                <Box sx={{px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', position: 'relative', flexShrink: 0}}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search people, products, charts…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{color: 'text.disabled'}}/>
                                </InputAdornment>
                            ),
                            endAdornment: searchLoading && searchFocused
                                ? <InputAdornment position="end"><CircularProgress size={14}/></InputAdornment>
                                : null,
                        }}
                    />
                    {showDropdown && (
                        <Paper
                            elevation={4}
                            sx={{
                                position: 'absolute',
                                left: 12, right: 12,
                                top: 'calc(100% - 1px)',
                                zIndex: 10,
                                maxHeight: 280,
                                overflow: 'auto',
                            }}
                        >
                            {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                                    No matches found.
                                </Typography>
                            )}
                            {!searchLoading && !searchQuery.trim() && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                                    Type to search people, products, or charts.
                                </Typography>
                            )}
                            {!searchLoading && searchResults.length > 0 && (
                                <List dense disablePadding>
                                    {searchResults.map((hit) => {
                                        const isCreating = hit.type === 'person' && creatingId === hit.id;
                                        const icon = hit.type === 'chart'
                                            ? <AccountTreeOutlinedIcon fontSize="small"/>
                                            : hit.type === 'product'
                                                ? <DescriptionOutlinedIcon fontSize="small"/>
                                                : null;
                                        return (
                                            <ListItemButton
                                                key={`${hit.type}:${hit.id}`}
                                                disabled={isCreating}
                                                onClick={() => handleSearchSelect(hit)}
                                            >
                                                {hit.type === 'person' ? (
                                                    <ListItemAvatar>
                                                        <UserAvatar
                                                            user={{id: hit.id, displayName: hit.displayTitle}}
                                                            userId={hit.id}
                                                            sx={{width: 32, height: 32}}
                                                        />
                                                    </ListItemAvatar>
                                                ) : (
                                                    <ListItemAvatar sx={{minWidth: 40}}>
                                                        {icon}
                                                    </ListItemAvatar>
                                                )}
                                                <ListItemText
                                                    primary={hit.displayTitle}
                                                    secondary={hit.type === 'person'
                                                        ? (hit.email || hit.role || '')
                                                        : (hit.type === 'chart' ? 'Chart' : 'Product')}
                                                    primaryTypographyProps={{variant: 'body2', fontWeight: 500}}
                                                    secondaryTypographyProps={{variant: 'caption'}}
                                                />
                                                <Chip
                                                    label={hit.type}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ml: 1, textTransform: 'capitalize', height: 22, fontSize: '0.68rem'}}
                                                />
                                                {isCreating && <CircularProgress size={14} sx={{ml: 1, flexShrink: 0}}/>}
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            )}
                        </Paper>
                    )}
                </Box>
            </ClickAwayListener>

            <List dense sx={{overflowY: 'auto', flex: 1, p: 0}}>
                {(loading || groupsLoading) && (
                    <ListItem><ListItemText primary="Loading…"/></ListItem>
                )}
                {(error || groupsError) && (
                    <ListItem>
                        <ListItemText
                            primary="Error loading team inbox"
                            secondary={(error || groupsError)?.message}
                        />
                    </ListItem>
                )}

                {!loading && !groupsLoading && allGroups.map((g) => {
                    const label = g.label || formatEntityGroupLabel(g.entityType, g.displayTitle);
                    const secondary = g.lastMessageText || 'No messages yet';
                    const icon = g.entityType === 'product'
                        ? <DescriptionOutlinedIcon fontSize="small"/>
                        : <AccountTreeOutlinedIcon fontSize="small"/>;
                    return (
                        <ListItemButton
                            key={entityGroupKey(g)}
                            sx={{px: 1.5, py: 0.5}}
                            onClick={() => handleOpenEntityGroup(g.entityType, g.entityId, g.displayTitle)}
                        >
                            <ListItemAvatar sx={{minWidth: 36}}>{icon}</ListItemAvatar>
                            <ListItemText
                                primary={label}
                                secondary={secondary}
                                primaryTypographyProps={{variant: 'body2', fontWeight: 500, noWrap: true}}
                                secondaryTypographyProps={{variant: 'caption', noWrap: true}}
                            />
                        </ListItemButton>
                    );
                })}

                {!loading && !error && chats.map((c) => {
                    const parts = c.participants || [];
                    let otherUid = null;
                    if (parts.length === 2 && myUid) {
                        otherUid = parts[0] === myUid ? parts[1] : parts[0];
                    } else if (parts.length > 0) {
                        otherUid = parts.find((p) => p !== myUid) || parts[0];
                    }
                    const participantMeta = otherUid ? (c[`participant_${otherUid}`] || null) : null;
                    const name = participantMeta?.displayName || c.name || c.title || 'Unknown';
                    const lastMessageText = c.lastMessage
                        ? (c.lastMessage.text || '')
                        : (c.lastMessageText || c.preview || '');
                    const otherUserObj = otherUid
                        ? {...participantMeta, id: otherUid, uid: otherUid, userId: otherUid, displayName: name, hasAvatar: true}
                        : null;
                    const isDeleting = deletingChatId === c.id;
                    return (
                        <ListItemButton
                            key={c.id}
                            disabled={isDeleting}
                            sx={{px: 1.5, py: 0.5, '&:hover .chat-del': {opacity: 1}}}
                            onClick={() => {
                                clearActiveEntityGroup();
                                setActiveChat(c);
                            }}
                        >
                            <ListItemAvatar>
                                <UserAvatar user={otherUserObj} userId={otherUid} sx={{width: 28, height: 28}}/>
                            </ListItemAvatar>
                            <ListItemText
                                primary={name}
                                secondary={lastMessageText}
                                primaryTypographyProps={{variant: 'body2', fontWeight: 500, noWrap: true}}
                                secondaryTypographyProps={{variant: 'caption', noWrap: true}}
                            />
                            {isDeleting
                                ? <CircularProgress size={14} sx={{ml: 0.5, flexShrink: 0}}/>
                                : (
                                    <Tooltip title="Delete" placement="left" enterDelay={600} enterNextDelay={600}>
                                        <IconButton
                                            size="small"
                                            className="chat-del"
                                            onClick={(e) => handleDeleteChat(e, c.id)}
                                            sx={{
                                                opacity: 0,
                                                transition: 'opacity 150ms',
                                                flexShrink: 0,
                                                color: 'text.disabled',
                                                '&:hover': {color: 'error.main'},
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                )
                            }
                        </ListItemButton>
                    );
                })}

                {!loading && !groupsLoading && !error && !groupsError && chats.length === 0 && allGroups.length === 0 && (
                    <ListItem><ListItemText primary="No chats or groups yet"/></ListItem>
                )}
            </List>
        </Box>
    );
}
